import type { InputService } from "./input-service";
import type { InputPoint } from "./types";
import { isInteractiveControlTarget } from "./interactive-target";

export interface InputBinding {
  destroy(): void;
}

function localPoint(surface: HTMLElement, clientX: number, clientY: number): InputPoint {
  const rect = typeof surface.getBoundingClientRect === "function"
    ? surface.getBoundingClientRect()
    : ({ left: 0, top: 0 } as DOMRect);
  return { x: clientX - rect.left, y: clientY - rect.top };
}

export function bindPointerInput(
  surface: HTMLElement,
  scope: Window,
  document: Document,
  input: InputService,
): InputBinding {
  const onPointerDown = (event: PointerEvent): void => {
    if (isInteractiveControlTarget(event.target)) return;
    event.preventDefault();
    input.press(event.pointerId, localPoint(surface, event.clientX, event.clientY));
    try {
      surface.setPointerCapture(event.pointerId);
    } catch {
      // Some embedded Chromium builds expose capture but reject a specific pointer.
    }
  };
  const onPointerMove = (event: PointerEvent): void => {
    if (isInteractiveControlTarget(event.target)) return;
    event.preventDefault();
    input.move(event.pointerId, localPoint(surface, event.clientX, event.clientY));
  };
  const onPointerUp = (event: PointerEvent): void => {
    if (isInteractiveControlTarget(event.target)) return;
    event.preventDefault();
    input.release(event.pointerId);
  };
  const onPointerCancel = (event: PointerEvent): void => {
    if (isInteractiveControlTarget(event.target)) return;
    event.preventDefault();
    input.cancel(event.pointerId);
  };
  const onLostPointerCapture = (event: PointerEvent): void => {
    input.cancel(event.pointerId);
  };
  const releaseAll = (): void => input.releaseAll();
  const onVisibilityChange = (): void => {
    if (document.visibilityState === "hidden") input.releaseAll();
  };

  surface.addEventListener("pointerdown", onPointerDown, { passive: false });
  surface.addEventListener("pointermove", onPointerMove, { passive: false });
  surface.addEventListener("pointerup", onPointerUp, { passive: false });
  surface.addEventListener("pointercancel", onPointerCancel, { passive: false });
  surface.addEventListener("lostpointercapture", onLostPointerCapture);
  scope.addEventListener("blur", releaseAll);
  document.addEventListener("visibilitychange", onVisibilityChange);

  return {
    destroy(): void {
      surface.removeEventListener("pointerdown", onPointerDown);
      surface.removeEventListener("pointermove", onPointerMove);
      surface.removeEventListener("pointerup", onPointerUp);
      surface.removeEventListener("pointercancel", onPointerCancel);
      surface.removeEventListener("lostpointercapture", onLostPointerCapture);
      scope.removeEventListener("blur", releaseAll);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      input.releaseAll();
    },
  };
}
